import React, { FC, useEffect, useMemo, useState, useRef } from "react";
import CtaBubble from "./CtaBubble";

export interface Props {
    readonly visible: boolean;
    /**
     * How long to display the bubble, in MS
     */
    readonly timeout?: number;
    /**
     * How long to delay showing the bubble, in MS
     */
    readonly delay?: number;
    readonly animate?: boolean;
    readonly buttonAnimation?: string;
    readonly dismissible?: boolean;
    onClick?: () => void;
    onDismiss?: () => void;
}

const CtaBubbleContainer: FC<React.PropsWithChildren<Props>> = props => {
    const { visible, timeout, delay, animate, buttonAnimation, dismissible, onClick, onDismiss, children } = props;

    const startTime = useMemo<number | undefined>(() => {
        return visible ? new Date().valueOf() : undefined;
    }, [visible]);

    const [showBubble, setShowBubble] = useState(false);
    const isMounted = useRef(true);

    useEffect(() => {
        let hideTimer: NodeJS.Timeout | null = null;

        if (startTime) {
            const delayTimer = setTimeout(() => {
                if (isMounted.current) {
                    setShowBubble(true);
                }

                if (typeof timeout === "number") {
                    hideTimer = setTimeout(() => {
                        if (isMounted.current) {
                            setShowBubble(false);
                        }
                    }, timeout);
                }
            }, delay);

            return () => {
                if (hideTimer) {
                    clearTimeout(hideTimer);
                }
                clearTimeout(delayTimer);
                isMounted.current = false;
            };
        }

        // Return a cleanup function for the case where startTime is undefined
        return () => {
            if (hideTimer) {
                clearTimeout(hideTimer);
            }
            isMounted.current = false;
        };
    }, [startTime, timeout, delay]);

    const handleDismiss = () => {
        setShowBubble(false);
        if (onDismiss) {
            onDismiss();
        }
    };

    return (
        <>
            {visible && showBubble && (
                <CtaBubble 
                    onClick={onClick} 
                    animate={animate} 
                    buttonAnimation={buttonAnimation}
                    dismissible={dismissible}
                    onDismiss={handleDismiss}
                >
                    {children}
                </CtaBubble>
            )}
        </>
    );
};

export default CtaBubbleContainer;